import { getRequestVerificationToken } from "./common";
import Department from "./types/Department";
import ProblemDetails from "./types/responses/ProblemDetails";
import UpdateDepartmentRequest from "./types/requests/UpdateDepartmentRequest";
import GeneralDepartmentPermitRequest from "./types/requests/GeneralDepartmentPermitRequest";
import DepartmentCreatedResponse from "./types/responses/DepartmentCreatedResponse";
import Result, { failedResult, successfulResult } from "./types/Result";
import WorkPermitSearchResult from "./types/WorkPermitSearchResult";
import CreateSignatureRequest from "./types/requests/CreateSignatureRequest";
import Signature from "./types/Signature";
import Position from "./types/Position";
import UpdateParticipantsRequest from "./types/requests/UpdateParticipantsRequest";
import GeneralDepartmentPermit, { GeneralDepartmentPermit_normalize } from "./types/permits/GeneralDepartmentPermit";
import { PermitType } from "./common/enums";
import EnergyHarbourPermit from "./types/permits/EnergyHarbourPermit";
import { normalizePermit, permitPageUrl } from "./common/permitUtils";
import CounterSignatureRequestRecipient from "./types/CounterSignatureRequestRecipient";
import CreateCounterSignatureRequestPostRequest from "./types/requests/CreateCounterSignatureRequestPostRequest";
import HotWorkConditionsRequest from "./types/requests/HotWorkRequest";
import UpdateEnergyHarbourPermitParticipantsRequest from "./types/requests/UpdateEnergyHarbourPermitParticipantsRequest";
import { PermitDocument, PermitRequest } from "./types/permits";
import AnonymousPermitRequest from "./types/requests/AnonymousPermitRequest";
import PaginatedBasePermits from "./types/responses/PaginatedBasePermits";
import { BaseWorkPermitInfo_normalize } from "./types/BaseWorkPermitInfo";
import { createQueryParams } from "./common/urlUtils";
import User from "./types/User";
import BaseUserInfo from "./types/BaseUserInfo";
import EnergyHarbourPermitRequest from "./types/requests/EnergyHarbourPermitRequest";
import { PostDepartmentRequest } from "./types/requests/PostDepartmentRequest";
import { CompanySummary_normalize } from "./types/CompanySummary";
import { CompanyFilter, createCompanyFilterQueryParams } from "./types/filters/CompanyFilter";
import WorkPermitFilter, { WorkPermitFilter_toURLSearchParams } from "./types/filters/WorkPermitFilter";
import { CompanyRequest } from "./types/requests/CompanyRequest";
import { CompanyCreatedResponse } from "./types/responses/CompanyCreatedResponse";
import { Company, Company_normalize } from "./types/Company";
import { PaginatedCompanySummaries } from "./types/responses/PaginatedCompanySummaries";
import { CompanyDepartmentRequest } from "./types/requests/CompanyDepartmentRequest";
import CompanyDepartment from "./types/CompanyDepartment";
import { CompanyDepartmentSummary, CompanyDepartmentSummary_normalize } from "./types/CompanyDepartmentSummary";
import { DetailedUserInfo, DetailedUserInfo_normalize } from "./types/DetailedUserInfo";
import Attachment from "./types/Attachment";
import { CompanyOption_normalize, CompanyOption } from "./types/CompanyOption";
import { CompanyDepartmentOption, CompanyDepartmentOption_normalize } from "./types/CompanyDepartmentOption";
import UserRequest from "./types/requests/UserRequest";
import ChangePasswordRequest from "./types/requests/ChangePasswordRequest";
import { EnergyHarbourCustomer, EnergyHarbourCustomer_normalize } from "./types/EnergyHarbourCustomer";

export async function searchWorkPermitsForConnection(
    sequenceIdentifier: string,
    permitName: string
): Promise<Result<WorkPermitSearchResult[], ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när sökresultatet hämtades";

    const fetchResult = await getRequest<WorkPermitSearchResult[]>(
        `/api/workPermits/connection/search?searchText=${sequenceIdentifier}&permitName=${permitName}`,
        defaultErrorMessage
    );

    return fetchResult;
}

export async function postRejectPermit(workPermitId: number, rejectionComment: string): Promise<Result<void, string>> {
    const defaultErrorMessage = "Något gick fel när tillståndet nekades";

    const requestConfig: RequestInit = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            RequestVerificationToken: (document.getElementsByName("__RequestVerificationToken")[0] as HTMLInputElement)
                .value
        },
        body: JSON.stringify({ rejectionComment: rejectionComment })
    };

    const postResponse = await fetch(`/api/workPermit/${workPermitId}/reject`, requestConfig);

    if (!postResponse.ok) {
        let errorMessage: string;
        try {
            const errorJson = await postResponse.json();
            errorMessage = (Object.values(errorJson.errors)[0] as string[])[0];
        } catch (error) {
            console.error(error);
            errorMessage = defaultErrorMessage;
        }

        return failedResult(errorMessage);
    }

    return successfulResult(undefined);
}

export async function postRenewPermit(workPermitId: number): Promise<Result<string, ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när tillståndet förnyades";

    const postResponse = await postRequest<string>(`/api/workPermit/${workPermitId}/renew`, defaultErrorMessage);

    return postResponse;
}

export async function postPermitAttachment(
    sequenceIdentifier: string,
    formData: FormData
): Promise<Result<number, { status: number; message: string }>> {
    const defaultErrorMessage = "Något gick fel när bilagan laddades upp.";

    const requestConfig: RequestInit = {
        method: "POST",
        headers: {
            RequestVerificationToken: getRequestVerificationToken()
        },
        body: formData
    };

    try {
        const postResponse = await fetch(`/api/workPermit/${sequenceIdentifier}/attachment`, requestConfig);

        if (!postResponse.ok) {
            const errorMessage = (await postResponse.text()) || defaultErrorMessage;

            return failedResult({
                status: postResponse.status,
                message: errorMessage
            });
        }

        const createdAttachmentId = await postResponse.json() as number;

        return successfulResult(createdAttachmentId);
    } catch (error) {
        const errorMessage = (error as any).message || defaultErrorMessage;

        return failedResult({
            status: 0,
            message: errorMessage
        });
    }
}

export async function getAttachment(attachmentId: number): Promise<Result<Attachment, ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när bilagan hämtades.";

    const getResult = await getRequest<Attachment>(
        `/api/attachment/${attachmentId}`,
        defaultErrorMessage
    );

    return getResult;
}

export async function getAttachments(sequenceIdentifier: string): Promise<Result<Attachment[], ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när tillståndets bilagor hämtades.";

    const getResult = getRequest<Attachment[]>(
        `/api/workPermit/${sequenceIdentifier}/attachments`,
        defaultErrorMessage
    );

    return getResult;
}

export async function deleteAttachment(attachmentId: number): Promise<Result<void, ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när bilagan togs bort.";
    const apiEndpoint = `/api/attachment/${attachmentId}`;

    const deleteResult = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return deleteResult;
}

export async function postDepartment(
    postDepartmentRequest: PostDepartmentRequest
): Promise<Result<DepartmentCreatedResponse, ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när avdelningen skapades.";
    const apiEndpoint = "/api/departments";

    const postResult = await postRequest<DepartmentCreatedResponse>(
        apiEndpoint,
        defaultErrorMessage,
        postDepartmentRequest
    );

    return postResult;
}

export async function getDepartments(): Promise<Result<Department[], ProblemDetails>> {
    const defaultErrorMessage = "Något gick fel när avdelningarna hämtades.";
    const apiEndpoint = "/api/departments";

    const getResult = await getRequest<Department[]>(apiEndpoint, defaultErrorMessage);

    return getResult;
}

export async function updateDepartment(
    departmentId: number,
    updateDepartmentRequest: UpdateDepartmentRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/departments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen uppdaterades.";

    const putResult = await putRequest<void>(apiEndpoint, defaultErrorMessage, updateDepartmentRequest);

    return putResult;
}

export async function deleteDepartment(departmentId: number): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/departments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen togs bort.";

    const deleteResult = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return deleteResult;
}

export async function getDepartmentById(departmentId: number): Promise<Result<Department, ProblemDetails>> {
    const apiEndpoint = `/api/departments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen hämtades.";

    const getResult = await getRequest<Department>(apiEndpoint, defaultErrorMessage);

    return getResult;
}

export async function createGeneralDepartmentPermit(
    request: GeneralDepartmentPermitRequest
): Promise<Result<string, ProblemDetails>> {
    const apiEndpoint = "/api/generalDepartmentPermits";
    const defaultErrorMessage = "Något gick fel när tillståndet skapades.";

    const postResult = await postRequest<string>(apiEndpoint, defaultErrorMessage, request);

    return postResult;
}

export async function createPermitAsync(
    request: PermitRequest,
    permitType: PermitType
): Promise<Result<string, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}`;
    const defaultErrorMessage = "Något gick fel när tillståndet skapades.";

    const postResult = await postRequest<string>(apiEndpoint, defaultErrorMessage, request);

    return postResult;
}

export async function updatePermitAsync(
    workPermitId: number,
    request: PermitRequest,
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet uppdaterades.";

    const putResult = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return putResult;
}

export async function getGeneralDepartmentPermitByDocumentId(
    documentId: string
): Promise<Result<GeneralDepartmentPermit, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/${documentId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet hämtades.";

    const getResult = await getRequest<GeneralDepartmentPermit>(apiEndpoint, defaultErrorMessage);

    if (getResult.failed) {
        return getResult;
    }

    getResult.value = GeneralDepartmentPermit_normalize(getResult.value);

    return getResult;
}

export async function getGeneralDepartmentPermitBySequenceIdentifier(
    sequenceIdentifier: string
): Promise<Result<GeneralDepartmentPermit, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/sequenceIdentifier/${sequenceIdentifier}`;
    const defaultErrorMessage = "Något gick fel när tillståndet hämtades.";

    const getResult = await getRequest<GeneralDepartmentPermit>(apiEndpoint, defaultErrorMessage);

    if (getResult.failed) {
        return getResult;
    }

    getResult.value = GeneralDepartmentPermit_normalize(getResult.value);

    return getResult;
}

export async function getPermitDocumentById<TDocument extends PermitDocument>(
    documentId: string,
    permitType: PermitType
): Promise<Result<TDocument, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}?documentReferenceId=${documentId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet hämtades.";

    const getResult = await getRequest<TDocument>(apiEndpoint, defaultErrorMessage);

    if (!getResult.failed) {
        getResult.value = normalizePermit(getResult.value);
    }

    return getResult;
}

export async function getPermitDocumentBySequenceIdentifier<TDocument extends PermitDocument>(
    sequenceIdentifier: string,
    permitType: PermitType
) {
    const apiEndpoint = `${permitApiBase(permitType)}?sequenceIdentifier=${sequenceIdentifier}`;
    const defaultErrorMessage = "Något gick fel när tillståndet hämtades.";

    const getResult = await getRequest<TDocument>(apiEndpoint, defaultErrorMessage);

    if (!getResult.failed) {
        getResult.value = normalizePermit(getResult.value);
    }

    return getResult;
}

export async function getPermitDocumentByWorKPermitId<TDocument extends PermitDocument>(
    workPermitId: number,
    permitType: PermitType
): Promise<Result<TDocument, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}?workPermitId=${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet hämtades.";

    const getResult = await getRequest<TDocument>(apiEndpoint, defaultErrorMessage);

    if (!getResult.failed) {
        getResult.value = normalizePermit(getResult.value);
    }

    return getResult;
}

export async function updateGeneralDepartmentPermit(
    permitDocumentId: string,
    request: GeneralDepartmentPermitRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/${permitDocumentId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet uppdaterades.";

    const updateResult = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return updateResult;
}

export async function deleteSignatureAsync(signature: Signature): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/signatures/${signature.id}`;
    const defaultErrorMessage = "Något gick fel när signaturen togs bort.";

    const deleteResult = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return deleteResult;
}

export async function deleteBase64SignatureAsync(
    workPermitId: number,
    signature: Signature
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/workPermits/${workPermitId}/signatures/legacy/${signature.signatureType}`;
    const defaultErrorMessage = "Något gick fel när signaturen togs bort.";

    const deleteResult = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return deleteResult;
}

export async function updatePermitAgreementEndDateAsync(
    workPermitId: number,
    updatedEndDate: Date
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(PermitType.EnergyHarbourPermit)}/${workPermitId}/permitAgreement/endDate`;
    const defaultErrorMessage = "Något gick fel när tillståndsversionens sluttid uppdaterades.";

    const putResult = await putRequest<void>(apiEndpoint, defaultErrorMessage, updatedEndDate);

    return putResult;
}

export async function createSignatureAsync(request: CreateSignatureRequest): Promise<Result<number, string>> {
    const apiEndpoint = "/api/signatures";
    const defaultErrorMessage = "Något gick fel när signaturen sparades.";

    const formData = new FormData();
    formData.append("signature", request.signatureBlob);
    formData.append("workPermitId", request.workPermitId.toString());
    formData.append("signatureType", request.signatureType);
    formData.append("nameClarification", request.nameClarification);

    if (request.phoneNumber) {
        formData.append("phoneNumber", request.phoneNumber);
    }

    const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
            RequestVerificationToken: getRequestVerificationToken()
        },
        body: formData
    });

    if (!response.ok) {
        const errorMessage = (await response.text()) || defaultErrorMessage;

        return failedResult(errorMessage);
    }

    const signatureId = await response.json();

    return successfulResult(signatureId);
}

export async function getSignatureBlob(signatureId: number): Promise<Result<Blob, ProblemDetails>> {
    const apiEndpoint = `/api/signatures/${signatureId}/blob`;
    const defaultErrorMessage = "Något gick fel när signaturen hämtades.";

    const response = await fetch(apiEndpoint);

    if (!response.ok) {
        let problemDetails: ProblemDetails;

        const contentType = response.headers.get("content-type");
        const errorContainsJson = !!contentType && contentType.includes("application/problem+json");

        if (errorContainsJson) {
            problemDetails = await response.json();
        } else {
            const errorMessage = (await response.text()) ?? defaultErrorMessage;
            problemDetails = {
                errors: {},
                status: response.status,
                title: errorMessage
            };
        }

        return failedResult(problemDetails);
    }

    const blob = await response.blob();

    return successfulResult(blob);
}

export async function acceptGeneralDepartmentPermit(workPermitId: number): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/accept/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet godkändes.";

    const acceptResult = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return acceptResult;
}

export async function updatePermitAgreementEndTimeOnAcceptedGeneralDepartmentPermit(
    permitDocumentId: string,
    updatedEndTime: Date
) {
    const apiEndpoint = `/api/generalDepartmentPermits/updatePermitAgreementEndTime/${permitDocumentId}`;
    const defaultErrorMessage = "Något gick fel när tillståndets sluttid uppdaterades.";

    const request = {
        dateTime: updatedEndTime
    };

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updateGeneralDepartmentPermitPositionActiveStatus(
    workPermitId: number,
    positions: Position[]
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/positions/isActive/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när positionerna updaterades.";

    const request = {
        positions
    };

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updateGeneralDepartmentPermitParticipants(
    workPermitId: number,
    request: UpdateParticipantsRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/participants/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när deltagarna uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updatePermitParticipantsAsync(
    workPermitId: number,
    request: UpdateParticipantsRequest,
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/participants`;
    const defaultErrorMessage = "Något gick fel när deltagarna uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function finishGeneralDepartmentPermit(workPermitId: number): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/generalDepartmentPermits/finish/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet avslutades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function deletePermit(workPermitId: number): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/workPermits/${workPermitId}`;
    const defaultErrorMessage = "Något gick fel när tillståndet togs bort.";

    const result = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function deactivateUser(userId: string): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}/deactivate`;
    const defaultErrorMessage = "Något gick fel när användaren inaktiverades";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function activateUser(userId: string): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}/activate`;
    const defaultErrorMessage = "Något gick fel när användaren aktiverades";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function acceptPermitAsync(
    workPermitId: number,
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/accept`;
    const defaultErrorMessage = "Något gick fel när tillståndet godkändes";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function finishPermitAsync(
    workPermitId: number,
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/finish/`;
    const defaultErrorMessage = "Något gick fel när tillståndet avslutades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function checkoutPermitAsync(
    workPermitId: number,
    permitType: PermitType
): Promise<Result<number, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/checkout`;
    const defaultErrorMessage = "Något gick fel när tillståndet checkades ut.";

    const result = await putRequest<number>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function renewPermitAsync(
    workPermitId: number,
    permitType: PermitType
): Promise<Result<string, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/renew`;
    const defaultErrorMessage = "Något gick fel när tillståndet förnyades.";

    const result = await putRequest<string>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function searchCounterSignatureRequestRecipients(
    searchInput: string
): Promise<Result<CounterSignatureRequestRecipient[], ProblemDetails>> {
    const apiEndpoint = `/api/counterSignaturerequests/recipients?query=${searchInput}`;
    const defaultErrorMessage = "Något gick vid sökning efter mottagare för kontrasignaturförfrågan.";

    const result = await getRequest<CounterSignatureRequestRecipient[]>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function sendCounterSignatureRequestAsync(
    request: CreateCounterSignatureRequestPostRequest
): Promise<Result<number, ProblemDetails>> {
    const apiEndpoint = "/api/counterSignaturerequests";
    const defaultErrorMessage = "Något gick fel när kontrasignaturförfrågan skapades.";

    const result = await postRequest<number>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function cancelCounterSignatureRequestAsync(
    counterSignatureRequestId: number
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/counterSignaturerequests/${counterSignatureRequestId}/cancel`;
    const defaultErrorMessage = "Något gick fel när kontrasignaturförfrågan avbröts.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function updateEnergyHarbourHotWorkAsync(
    documentPermitId: string,
    request: EnergyHarbourPermitRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/energyHarbourPermits/${documentPermitId}/hotWork`;
    const defaultErrorMessage = "Något gick fel när heta arbeten uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updatePositionsAsync(
    workPermitId: number,
    positions: Position[],
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/positions`;
    const defaultErrorMessage = "Något gick fel när positionerna updaterades.";

    const request = {
        positions
    };

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function rejectPermitAsync(
    workPermitId: number,
    rejectionComment: string,
    permitType: PermitType
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/reject`;
    const defaultErrorMessage = "Något gick fel när tillståndet avslogs.";

    const request = {
        rejectionComment
    };

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updateEnergyHarbourPermitParticipantsAsync(
    workpermitId: number,
    request: UpdateEnergyHarbourPermitParticipantsRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/energyHarbourPermits/${workpermitId}/participants`;
    const defaultErrorMessage = "Något gick fel när deltagarna uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function updateAdminCommentAsync(
    workPermitId: number,
    updatedAdminComment: string,
    permitType: PermitType
) {
    const apiEndpoint = `${permitApiBase(permitType)}/${workPermitId}/adminComment`;
    const defaultErrorMessage = "Något gick fel när kommentarerna uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, updatedAdminComment);

    return result;
}

export async function getPaginatedBasePermitInfoByFilterAsync(
    filter: WorkPermitFilter
): Promise<Result<PaginatedBasePermits, ProblemDetails>> {
    const urlSearchParams = WorkPermitFilter_toURLSearchParams(filter);
    const queryParamsString = urlSearchParams.toString();

    const apiEndpoint = `/api/workPermits/paginated?${queryParamsString}`;
    const defaultErrorMessage = "Något gick fel när tillståndslistan hämtades.";

    const result = await getRequest<PaginatedBasePermits>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value.permits = result.value.permits.map(BaseWorkPermitInfo_normalize);
    }

    return result;
}

export async function getBaseUserInfoById(userId: string): Promise<Result<BaseUserInfo, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}/baseUserInfo`;
    const defaultErrorMessage = "Något gick fel när användaren hämtades.";

    const result = await getRequest<BaseUserInfo>(apiEndpoint, defaultErrorMessage);

    if (result.failed) {
        return result;
    }

    return result;
}

export async function getCompanySummariesByFilterAsync(
    filter: CompanyFilter
): Promise<Result<PaginatedCompanySummaries, ProblemDetails>> {
    const urlSearchParams = createCompanyFilterQueryParams(filter);

    const result = await getRequest<PaginatedCompanySummaries>(
        `/api/companies?${urlSearchParams.toString()}`,
        "Något gick fel när företagen hämtades."
    );

    if (!result.failed) {
        result.value.companies = result.value.companies.map(CompanySummary_normalize);
    }

    return result;
}

export async function createCompanyAsync(
    request: CompanyRequest
): Promise<Result<CompanyCreatedResponse, ProblemDetails>> {
    const apiEndpoint = "/api/companies";
    const defaultErrorMessage = "Något gick fel när företaget skapades.";

    const result = await postRequest<CompanyCreatedResponse>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function getCompanyByIdAsync(companyId: string): Promise<Result<Company, ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyId}`;
    const defaultErrorMessage = "Något gick fel när företaget hämtades.";

    const result = await getRequest<Company>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = Company_normalize(result.value);
    }

    return result;
}

export async function getCompanyDepartmentSummariesByCompanyIdAsync(
    companyId: string
): Promise<Result<CompanyDepartmentSummary[], ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyId}/departments`;
    const defaultErrorMessage = "Något gick fel när företagets avdelningar hämtades.";

    const result = await getRequest<CompanyDepartmentSummary[]>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = result.value.map(CompanyDepartmentSummary_normalize);
    }

    return result;
}

export async function updateCompanyAsync(
    companyId: string,
    request: CompanyRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyId}`;
    const defaultErrorMessage = "Något gick fel när företaget uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function deleteCompanyAsync(companyId: string): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyId}`;
    const defaultErrorMessage = "Något gick fel när företaget togs bort.";

    const result = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function createCompanyDepartmentAsync(
    companyId: string,
    request: CompanyDepartmentRequest
): Promise<Result<DepartmentCreatedResponse, ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyId}/departments`;
    const defaultErrorMessage = "Något gick fel när avdelningen skapades.";

    const result = await postRequest<DepartmentCreatedResponse>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function getCompanyDepartmentByIdAsync(
    departmentId: number
): Promise<Result<CompanyDepartment, ProblemDetails>> {
    const apiEndpoint = `/api/companyDepartments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen hämtades.";

    const result = await getRequest<CompanyDepartment>(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function updateCompanyDepartmentAsync(
    departmentId: number,
    request: CompanyDepartmentRequest
): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/companyDepartments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen uppdaterades.";

    const result = await putRequest<void>(apiEndpoint, defaultErrorMessage, request);

    return result;
}

export async function deleteCompanyDepartmentAsync(departmentId: number): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/companyDepartments/${departmentId}`;
    const defaultErrorMessage = "Något gick fel när avdelningen togs bort.";

    const result = await deleteRequest(apiEndpoint, defaultErrorMessage);

    return result;
}

export async function getDetailedUserInfoAsync(userId: string): Promise<Result<DetailedUserInfo, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}/detailedUserInfo`;
    const defaultErrorMessage = "Något gick fel när användaren hämtades.";

    const result = await getRequest<DetailedUserInfo>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = DetailedUserInfo_normalize(result.value);
    }

    return result;
}

export async function createUserAsync(request: UserRequest): Promise<Result<{ createdUserId: string }, ProblemDetails>> {
    const apiEndpoint = "/api/users";
    const defaultErrorMessage = "Något gick fel när användaren skapades.";

    return await postRequest<{ createdUserId: string }>(apiEndpoint, defaultErrorMessage, request);
}

export async function updateUserAsync(userId: string, request: UserRequest): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}`;
    const defaultErrorMessage = "Något gick fel när användaren uppdaterades.";

    return await putRequest<void>(apiEndpoint, defaultErrorMessage, request);
}

export async function changePasswordAsync(userId: string, request: ChangePasswordRequest): Promise<Result<void, ProblemDetails>> {
    const apiEndpoint = `/api/users/${userId}/password`;
    const defaultErrorMessage = "Något gick fel när lösenordet ändrades.";

    return await putRequest<void>(apiEndpoint, defaultErrorMessage, request);
}

export async function getCompanyOptionsAsync(searchText: string): Promise<Result<CompanyOption[], ProblemDetails>> {
    const apiEndpoint = `/api/companies/options?searchText=${encodeURIComponent(searchText)}`;
    const defaultErrorMessage = "Något gick fel när företagen hämtades.";

    const result = await getRequest<CompanyOption[]>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = result.value.map(CompanyOption_normalize);
    }

    return result;
}

export async function getCompanyDepartmentOptionsAsync(companyid: string): Promise<Result<CompanyDepartmentOption[], ProblemDetails>> {
    const apiEndpoint = `/api/companies/${companyid}/departmentoptions`;
    const defaultErrorMessage = "Något gick fel när avdelningarna hämtades.";

    const result = await getRequest<CompanyDepartmentOption[]>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = result.value.map(CompanyDepartmentOption_normalize);
    }
 
    return result;
}

export async function getEnergyHarbourCustomersAsync(): Promise<Result<EnergyHarbourCustomer[], ProblemDetails>> {
    const apiEndpoint = "/api/energyHarbourPermits/customers";
    const defaultErrorMessage = "Något gick fel när kunderna hämtades.";

    const result = await getRequest<EnergyHarbourCustomer[]>(apiEndpoint, defaultErrorMessage);

    if (!result.failed) {
        result.value = result.value.map(EnergyHarbourCustomer_normalize);
    }

    return result;
}

async function getRequest<TValue>(
    apiEndpoint: string,
    defaultErrorMessage: string
): Promise<Result<TValue, ProblemDetails>> {
    try {
        const fetchResponse = await fetch(apiEndpoint);

        if (!fetchResponse.ok) {
            let problemDetails: ProblemDetails;

            if (responseContainsProblemJson(fetchResponse)) {
                problemDetails = await fetchResponse.json();
            } else {
                const errorMessage = (await fetchResponse.text()) ?? defaultErrorMessage;
                problemDetails = {
                    errors: {},
                    status: fetchResponse.status,
                    title: errorMessage
                };
            }

            return failedResult(problemDetails);
        }

        const fetchedData: TValue = responseContainsJson(fetchResponse)
            ? await fetchResponse.json()
            : await fetchResponse.text();

        return successfulResult(fetchedData);
    } catch (error) {
        const errorMessage = (error as any).message || defaultErrorMessage;
        const problemDetails = {
            errors: {},
            status: 500,
            title: errorMessage
        };

        return failedResult(problemDetails);
    }
}

async function postRequest<TResponse>(
    apiEndpoint: string,
    defaultErrorMessage: string,
    body: any = {}
): Promise<Result<TResponse, ProblemDetails>> {
    const requestConfig: RequestInit = {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            RequestVerificationToken: getRequestVerificationToken()
        },
        body: JSON.stringify(body)
    };

    try {
        const postResponse = await fetch(apiEndpoint, requestConfig);

        if (!postResponse.ok) {
            let problemDetails: ProblemDetails;

            if (responseContainsProblemJson(postResponse)) {
                problemDetails = await postResponse.json();
            } else {
                const errorMessage = (await postResponse.text()) ?? defaultErrorMessage;
                problemDetails = {
                    errors: {},
                    status: postResponse.status,
                    title: errorMessage
                };
            }

            console.error(problemDetails);

            return failedResult(problemDetails);
        }

        const responseValue = responseContainsJson(postResponse)
            ? await postResponse.json()
            : await postResponse.text();

        return successfulResult(responseValue);
    } catch (error) {
        const errorMessage = (error as any).message || defaultErrorMessage;

        const problemDetails: ProblemDetails = {
            errors: {},
            status: 500,
            title: errorMessage
        };

        console.error(problemDetails);

        return failedResult(problemDetails);
    }
}

async function putRequest<TResponse>(
    apiEndpoint: string,
    defaultErrorMessage: string,
    body: any = {}
): Promise<Result<TResponse, ProblemDetails>> {
    const requestConfig: RequestInit = {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            RequestVerificationToken: getRequestVerificationToken()
        },
        body: JSON.stringify(body)
    };

    try {
        const putResponse = await fetch(apiEndpoint, requestConfig);

        if (!putResponse.ok) {
            let problemDetails: ProblemDetails;
            let errorMessage: string;

            if (responseContainsProblemJson(putResponse)) {
                problemDetails = await putResponse.json();
                errorMessage = !!problemDetails && !!problemDetails.title ? problemDetails.title : defaultErrorMessage;
            } else {
                errorMessage = (await putResponse.text()) ?? defaultErrorMessage;
                problemDetails = {
                    errors: {},
                    status: putResponse.status,
                    title: errorMessage
                };
            }

            console.error(problemDetails);

            return failedResult(problemDetails);
        }

        const responseValue = responseContainsJson(putResponse) ? await putResponse.json() : await putResponse.text();

        return successfulResult(responseValue);
    } catch (error) {
        const errorMessage = (error as any).message || defaultErrorMessage;

        const problemDetails: ProblemDetails = {
            errors: {},
            status: 500,
            title: errorMessage
        };

        console.error(problemDetails);

        return failedResult(problemDetails);
    }
}

export async function deleteRequest(
    apiEndpoint: string,
    defaultErrorMessage: string
): Promise<Result<void, ProblemDetails>> {
    const requestConfig: RequestInit = {
        method: "DELETE",
        headers: {
            RequestVerificationToken: getRequestVerificationToken()
        }
    };

    let deleteResponse: Response;
    try {
        deleteResponse = await fetch(apiEndpoint, requestConfig);
    } catch (error) {
        const errorMessage = (error as any).message || defaultErrorMessage;

        const problemDetails: ProblemDetails = {
            errors: {},
            status: 500,
            title: errorMessage
        };

        return failedResult(problemDetails);
    }

    if (!deleteResponse.ok) {
        let problemDetails: ProblemDetails;

        if (responseContainsProblemJson(deleteResponse)) {
            problemDetails = await deleteResponse.json();
        } else {
            const errorMessage = (await deleteResponse.text()) ?? defaultErrorMessage;
            problemDetails = {
                errors: {},
                status: deleteResponse.status,
                title: errorMessage
            };
        }

        return failedResult(problemDetails);
    }

    return successfulResult(undefined);
}

function responseContainsJson(response: Response): boolean {
    const contentType = response.headers.get("content-type");

    if (!contentType) {
        return false;
    }

    const includesJson = contentType.includes("application/json");

    return includesJson;
}

function responseContainsProblemJson(response: Response): boolean {
    const contentType = response.headers.get("content-type");

    if (!contentType) {
        return false;
    }

    const includesJson = contentType.includes("application/problem+json");

    return includesJson;
}

function permitApiBase(permitType: PermitType): string {
    const permitApiBases: { [key in PermitType]: string } = {
        [PermitType.CoordinationPermit]: "/api/coordinationPermits",
        [PermitType.DivingPermit]: "/api/divingPermits",
        [PermitType.EnergyHarbourPermit]: "/api/energyHarbourPermits",
        [PermitType.GeneralDepartmentPermit]: "/api/generalDepartmentPermits",
        [PermitType.HotWorkOnShipPermit]: "/api/hotWorkOnShipPermits",
        [PermitType.HullCleaningPermit]: "/api/hullCleaningPermits",
        [PermitType.MaritimeEvent]: "/api/maritimeEvents",
        [PermitType.ShipRepairsPermit]: "/api/shipRepairsPermits",
        [PermitType.UnderWaterWorkPermit]: "/api/underWaterWorkPermits"
    };

    if (!permitApiBases[permitType]) {
        console.error(`No API base for permit type "${permitType}" has been defined.`);
        return "";
    }

    return permitApiBases[permitType];
}
